import React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@material-ui/core";
import { COLORS, WIZARD_STEPS } from "../constants";
import { Btn } from "../components/UI";
import { WizardDoneStep } from "../components/WizardDoneStep";
import { WizardTemplateStep } from "../components/WizardTemplateStep";
import { WizardLocationStep } from "../components/WizardLocationStep";
import { WizardModeStep } from "../components/WizardModeStep";
import { WizardReviewStep } from "../components/WizardReviewStep";
import { useStyles } from "./wizard-view.style";

function getStepBackground(wizardDone, stepIndex, currentStep) {
  if (wizardDone) return COLORS.success;
  return stepIndex <= currentStep ? COLORS.primary : COLORS.border;
}

const renderStepContent = ({ wizardDone, wizardStep, ...props }) => {
  if (wizardDone) return <WizardDoneStep {...props} />;
  if (wizardStep === 0) return <WizardTemplateStep {...props} />;
  if (wizardStep === 1) return <WizardLocationStep {...props} />;
  if (wizardStep === 2) return <WizardModeStep {...props} />;
  return <WizardReviewStep {...props} />;
};

const wizardSelectedTemplateShape = PropTypes.shape({
  name: PropTypes.string,
  services: PropTypes.array,
  resources: PropTypes.array,
});

export function WizardView({
  wizardStep,
  wizardDone,
  wizardApplyErrors,
  wizardUnresolvedServiceIds,
  wizardUnresolvedResourceIds,
  wizardTemplateId,
  setWizardTemplateId,
  wizardChecked,
  wizardSearch,
  setWizardSearch,
  wizardMode,
  setWizardMode,
  wizardSelectedTemplate,
  wizardLeafs,
  wizardOverlap,
  wizardNext,
  wizardPrev,
  wizardCanGo,
  wizardReset,
  wizardCheck,
  applyWizard,
  wizardStartApply,
  setTab,
  templates,
  openNewTemplate,
  treeExpanded,
  onTreeToggle,
  locations,
  servicesRef,
  resourcesRef,
}) {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Box className={classes.stepperBar}>
        <Box className={classes.stepperRow}>
          {WIZARD_STEPS.map((stepLabel, stepIndex) => {
            const skipMode = stepIndex === 2 && !wizardOverlap;
            const isActiveOrPast = stepIndex <= wizardStep || wizardDone;
            return (
              <Box
                key={stepLabel}
                className={classes.stepItem}
                sx={{ opacity: skipMode ? 0.35 : 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: '5px' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '12px',
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      background: getStepBackground(wizardDone, stepIndex, wizardStep),
                      color: isActiveOrPast ? COLORS.white : COLORS.muted,
                    }}
                  >
                    {wizardDone ? "\u2713" : stepIndex + 1}
                  </Box>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      fontWeight: stepIndex === wizardStep ? 700 : 400,
                      color: stepIndex <= wizardStep ? COLORS.primary : COLORS.muted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stepLabel}
                  </Typography>
                </Box>
                {stepIndex < 3 && (
                  <Box
                    className={classes.stepConnector}
                    sx={{ background: stepIndex < wizardStep ? COLORS.primary : COLORS.border }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box className={classes.scrollContent}>
        {renderStepContent({
          wizardDone,
          wizardStep,
          wizardSelectedTemplate,
          wizardLeafs,
          wizardMode,
          wizardApplyErrors,
          wizardUnresolvedServiceIds,
          wizardUnresolvedResourceIds,
          wizardReset,
          setTab,
          templates,
          wizardTemplateId,
          setWizardTemplateId,
          openNewTemplate,
          wizardSearch,
          setWizardSearch,
          wizardChecked,
          wizardCheck,
          treeExpanded,
          onTreeToggle,
          locations,
          setWizardMode,
          servicesRef,
          resourcesRef,
        })}
      </Box>
      {!wizardDone && (
        <Box className={classes.footer}>
          <Btn variant="outlined" onClick={wizardPrev} disabled={wizardStep === 0}>
            &larr; Back
          </Btn>
          <Box className={classes.footerRight}>
            <Btn variant="outlined" onClick={() => { setTab("browse"); wizardReset(); }}>
              Cancel
            </Btn>
            {wizardStep < 3 && (
              <Btn onClick={wizardNext} disabled={!wizardCanGo}>Next &rarr;</Btn>
            )}
            {wizardStep >= 3 && (
              <Btn onClick={applyWizard} sx={{ background: COLORS.successDark }}>&#10003; Apply</Btn>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

WizardView.propTypes = {
  wizardStep: PropTypes.number.isRequired,
  wizardDone: PropTypes.bool,
  wizardApplyErrors: PropTypes.array,
  wizardUnresolvedServiceIds: PropTypes.array,
  wizardUnresolvedResourceIds: PropTypes.array,
  wizardTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setWizardTemplateId: PropTypes.func.isRequired,
  wizardChecked: PropTypes.instanceOf(Set),
  wizardSearch: PropTypes.string,
  setWizardSearch: PropTypes.func.isRequired,
  wizardMode: PropTypes.string,
  setWizardMode: PropTypes.func.isRequired,
  wizardSelectedTemplate: wizardSelectedTemplateShape,
  wizardLeafs: PropTypes.array,
  wizardOverlap: PropTypes.bool,
  wizardNext: PropTypes.func.isRequired,
  wizardPrev: PropTypes.func.isRequired,
  wizardCanGo: PropTypes.bool,
  wizardReset: PropTypes.func.isRequired,
  wizardCheck: PropTypes.func.isRequired,
  applyWizard: PropTypes.func.isRequired,
  wizardStartApply: PropTypes.func.isRequired,
  setTab: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    desc: PropTypes.string,
    services: PropTypes.array,
    resources: PropTypes.array,
  })),
  openNewTemplate: PropTypes.func.isRequired,
  treeExpanded: PropTypes.instanceOf(Set),
  onTreeToggle: PropTypes.func.isRequired,
  locations: PropTypes.array,
  servicesRef: PropTypes.array,
  resourcesRef: PropTypes.array,
};

WizardView.defaultProps = {
  wizardDone: false,
  wizardApplyErrors: [],
  wizardUnresolvedServiceIds: [],
  wizardUnresolvedResourceIds: [],
  wizardTemplateId: null,
  wizardChecked: new Set(),
  wizardSearch: "",
  wizardMode: "merge",
  wizardSelectedTemplate: null,
  wizardLeafs: [],
  wizardOverlap: false,
  wizardCanGo: false,
  templates: [],
  treeExpanded: undefined,
  locations: [],
  servicesRef: [],
  resourcesRef: [],
};
